// src/app/tools/image-compressor/page.tsx
'use client';

import { useState, useRef, ChangeEvent } from 'react';
import { Upload, FileArchive, Download, Trash2, Image as ImageIcon } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { imageCompressorContent } from '../../data/image-compressor-content';
import Link from 'next/link';

interface ImageFile {
    file: File;
    preview: string;
    originalSize: number;
    compressedSize?: number;
    compressedBlob?: Blob | null;
    loading: boolean;
    error?: string;
}

export default function ImageCompressor() {
    const [images, setImages] = useState<ImageFile[]>([]);
    const [quality, setQuality] = useState<number>(70);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        processFiles(files);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = Array.from(e.dataTransfer.files);
        processFiles(files);
    };

    const processFiles = (files: File[]) => {
        const imageFiles = files.filter(file => file.type.startsWith('image/'));

        if (imageFiles.length === 0) {
            alert('Please upload valid image files (JPEG, PNG, WEBP, etc.)');
            return;
        }

        const newImages = imageFiles.map(file => ({
            file,
            preview: URL.createObjectURL(file),
            originalSize: file.size,
            loading: false,
            compressedBlob: null,
        }));

        setImages(prev => [...prev, ...newImages]);
    };

    const compressImage = (image: ImageFile, index: number) => {
        const img = new Image();
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;

        // Update loading state
        setImages(prev => {
            const newImages = [...prev];
            newImages[index] = { ...newImages[index], loading: true, error: undefined };
            return newImages;
        });

        img.onload = () => {
            try {
                // Calculate new dimensions while maintaining aspect ratio
                const maxWidth = 1920;
                const maxHeight = 1080;
                let width = img.width;
                let height = img.height;

                if (width > maxWidth) {
                    height = Math.round((height * maxWidth) / width);
                    width = maxWidth;
                }
                if (height > maxHeight) {
                    width = Math.round((width * maxHeight) / height);
                    height = maxHeight;
                }

                // Set canvas dimensions
                canvas.width = width;
                canvas.height = height;

                // Draw image on canvas with new dimensions
                ctx.drawImage(img, 0, 0, width, height);

                // Convert to blob with specified quality
                canvas.toBlob(
                    (blob) => {
                        if (!blob) {
                            throw new Error('Failed to compress image');
                        }

                        const compressedUrl = URL.createObjectURL(blob);

                        setImages(prev => {
                            const newImages = [...prev];
                            newImages[index] = {
                                ...newImages[index],
                                preview: compressedUrl,
                                compressedSize: blob.size,
                                compressedBlob: blob,
                                loading: false,
                            };
                            return newImages;
                        });
                    },
                    'image/jpeg',
                    quality / 100
                );
            } catch (error) {
                console.error('Error compressing image:', error);
                setImages(prev => {
                    const newImages = [...prev];
                    newImages[index] = {
                        ...newImages[index],
                        loading: false,
                        error: 'Failed to compress image',
                    };
                    return newImages;
                });
            }
        };

        img.onerror = () => {
            setImages(prev => {
                const newImages = [...prev];
                newImages[index] = {
                    ...newImages[index],
                    loading: false,
                    error: 'Failed to load image',
                };
                return newImages;
            });
        };

        img.src = image.preview;
    };

    const compressAll = () => {
        images.forEach((image, index) => {
            if (!image.compressedBlob) {
                compressImage(image, index);
            }
        });
    };

    const downloadImage = (image: ImageFile, index: number) => {
        if (!image.compressedBlob) {
            compressImage(image, index);
            return;
        }

        const link = document.createElement('a');
        const fileName = image.file.name.replace(/\.[^/.]+$/, '') + '-compressed.jpg';

        link.href = URL.createObjectURL(image.compressedBlob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const removeImage = (index: number) => {
        setImages(prev => {
            const newImages = [...prev];
            URL.revokeObjectURL(newImages[index].preview);
            newImages.splice(index, 1);
            return newImages;
        });
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const calculateTotalSavings = () => {
        let originalTotal = 0;
        let compressedTotal = 0;

        images.forEach(image => {
            originalTotal += image.originalSize;
            compressedTotal += image.compressedSize || image.originalSize;
        });

        const savings = originalTotal - compressedTotal;
        const percentage = (savings / originalTotal) * 100;

        return {
            original: formatFileSize(originalTotal),
            compressed: formatFileSize(compressedTotal),
            savings: formatFileSize(savings),
            percentage: percentage.toFixed(1),
        };
    };

    const clearAll = () => {
        images.forEach(image => {
            URL.revokeObjectURL(image.preview);
        });
        setImages([]);
    };

    const totalSavings = calculateTotalSavings();

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                    <ImageIcon className="h-8 w-8 text-blue-600" />
                </div>
                <ToolPageIntro title="Image Compressor">
                Compress images online for free with our smart image compression tool. Reduce file size while maintaining quality for faster websites and better user experience.
            </ToolPageIntro>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <div
                        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'
                            }`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <div className="flex flex-col items-center justify-center space-y-3">
                            <Upload className="h-12 w-12 text-gray-400" />
                            <div>
                                <p className="text-sm text-gray-600">
                                    <span className="font-medium text-blue-600">Click to upload</span> or drag and drop
                                </p>
                                <p className="text-xs text-gray-500">Supports JPG, PNG, WEBP (Max 10MB per image)</p>
                            </div>
                        </div>
                        <input
                            type="file"
                            ref={fileInputRef}
                            className="hidden"
                            accept="image/*"
                            multiple
                            onChange={handleFileChange}
                        />
                    </div>

                    {images.length > 0 && (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <h2 className="text-lg font-medium text-gray-900">
                                    {images.length} {images.length === 1 ? 'Image' : 'Images'} Uploaded
                                </h2>
                                <div className="flex space-x-3">
                                    <button
                                        onClick={clearAll}
                                        className="text-sm text-red-600 hover:text-red-800 flex items-center"
                                    >
                                        <Trash2 className="h-4 w-4 mr-1" /> Clear All
                                    </button>
                                    <button
                                        onClick={compressAll}
                                        disabled={images.every(img => img.compressedBlob)}
                                        className={`text-sm flex items-center ${images.every(img => img.compressedBlob)
                                            ? 'text-gray-400 cursor-not-allowed'
                                            : 'text-blue-600 hover:text-blue-800'
                                            }`}
                                    >
                                        < FileArchive className="h-4 w-4 mr-1" /> Compress All
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-4">
                                {images.map((image, index) => (
                                    <div
                                        key={index}
                                        className="border border-gray-200 rounded-lg overflow-hidden"
                                    >
                                        <div className="p-4 flex items-center space-x-4">
                                            <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-md overflow-hidden">
                                                <img
                                                    src={image.preview}
                                                    alt={`Preview ${index + 1}`}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-gray-900 truncate">
                                                    {image.file.name}
                                                </p>
                                                <div className="flex items-center space-x-4 mt-1">
                                                    <span className="text-xs text-gray-500">
                                                        {formatFileSize(image.originalSize)}
                                                    </span>
                                                    {image.compressedSize && (
                                                        <>
                                                            <span className="text-gray-300">→</span>
                                                            <span className="text-xs font-medium text-green-600">
                                                                {formatFileSize(image.compressedSize)}
                                                            </span>
                                                            <span className="text-xs text-gray-500">
                                                                ({Math.round((1 - image.compressedSize / image.originalSize) * 100)}% smaller)
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                {!image.compressedBlob ? (
                                                    <button
                                                        onClick={() => compressImage(image, index)}
                                                        disabled={image.loading}
                                                        className={`p-2 rounded-full ${image.loading
                                                            ? 'text-gray-400 cursor-not-allowed'
                                                            : 'text-blue-600 hover:bg-blue-50'
                                                            }`}
                                                        title="Compress"
                                                    >
                                                        < FileArchive className="h-5 w-5" />
                                                    </button>
                                                ) : (
                                                    <a
                                                        href={image.preview}
                                                        download={image.file.name.replace(/\.[^/.]+$/, '') + '-compressed.jpg'}
                                                        className="p-2 text-green-600 hover:bg-green-50 rounded-full"
                                                        title="Download"
                                                    >
                                                        <Download className="h-5 w-5" />
                                                    </a>
                                                )}
                                                <button
                                                    onClick={() => removeImage(index)}
                                                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full"
                                                    title="Remove"
                                                >
                                                    <Trash2 className="h-5 w-5" />
                                                </button>
                                            </div>
                                        </div>
                                        {image.loading && (
                                            <div className="h-1 w-full bg-gray-200 overflow-hidden">
                                                <div className="h-full bg-blue-500 animate-pulse"></div>
                                            </div>
                                        )}
                                        {image.error && (
                                            <div className="bg-red-50 text-red-600 text-xs p-2">
                                                {image.error}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="space-y-6">
                    <div className="bg-white rounded-xl shadow-sm p-6">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Compression Settings</h2>

                        <div className="space-y-4">
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label htmlFor="quality" className="block text-sm font-medium text-gray-700">
                                        Image Quality
                                    </label>
                                    <span className="text-sm font-medium text-gray-900">{quality}%</span>
                                </div>
                                <input
                                    type="range"
                                    id="quality"
                                    min="10"
                                    max="90"
                                    step="5"
                                    value={quality}
                                    onChange={(e) => setQuality(Number(e.target.value))}
                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                                />
                                <div className="flex justify-between text-xs text-gray-500 mt-1">
                                    <span>Smaller file</span>
                                    <span>Better quality</span>
                                </div>
                            </div>

                            <div className="pt-4 border-t border-gray-200">
                                <h3 className="text-sm font-medium text-gray-700 mb-3">Compression Summary</h3>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Original Size:</span>
                                        <span className="font-medium">{totalSavings.original}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-gray-600">Compressed Size:</span>
                                        <span className="font-medium text-green-600">{totalSavings.compressed}</span>
                                    </div>
                                    <div className="flex justify-between pt-2 border-t border-gray-100">
                                        <span className="text-gray-600">Total Savings:</span>
                                        <span className="font-medium text-blue-600">
                                            {totalSavings.savings} ({totalSavings.percentage}%)
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-md">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg
                                    className="h-5 w-5 text-blue-400"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-blue-800">Tips for Best Results</h3>
                                <div className="mt-2 text-sm text-blue-700 space-y-1">
                                    <p>• For web use, aim for 60-80% quality</p>
                                    <p>• Use the preview to check quality before downloading</p>
                                    <p>• Try different quality settings to find the best balance</p>
                                    <p>• Original images are never uploaded to any server</p>
                                </div>

                                <div className="pt-4 border-t border-gray-200">
                                    <h3 className="text-sm font-medium text-gray-700 mb-3">Compression Summary</h3>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Original Size:</span>
                                            <span className="font-medium">{totalSavings.original}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Compressed Size:</span>
                                            <span className="font-medium text-green-600">{totalSavings.compressed}</span>
                                        </div>
                                        <div className="flex justify-between pt-2 border-t border-gray-100">
                                            <span className="text-gray-600">Total Savings:</span>
                                            <span className="font-medium text-blue-600">
                                                {totalSavings.savings} ({totalSavings.percentage}%)
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-md">
                            <div className="flex">
                                <div className="flex-shrink-0">
                                    <svg
                                        className="h-5 w-5 text-blue-400"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                </div>
                                <div className="ml-3">
                                    <h3 className="text-sm font-medium text-blue-800">Tips for Best Results</h3>
                                    <div className="mt-2 text-sm text-blue-700 space-y-1">
                                        <p>• For web use, aim for 60-80% quality</p>
                                        <p>• Use the preview to check quality before downloading</p>
                                        <p>• Try different quality settings to find the best balance</p>
                                        <p>• Original images are never uploaded to any server</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Informational Content */}
            <div className="mt-12">
                <InformationalContent
                    title={imageCompressorContent.title}
                    description={imageCompressorContent.description}
                    content={imageCompressorContent.content}
                    faqs={imageCompressorContent.faqs}
                    relatedTools={imageCompressorContent.relatedTools}
                />
            </div>
        </div>
    );
}